import { useState, useEffect } from 'react';
import { collection, getDocs, doc, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { ObjectCategory } from '../types/labelSchema';

export function useObjectCategories() {
  const [categories, setCategories] = useState<ObjectCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    
    const loadCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Loading object categories...');
        const snapshot = await getDocs(collection(db, 'objectCategories'));
        
        if (mounted) {
          const categoriesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as ObjectCategory[];
          
          setCategories(categoriesData);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading object categories:', err);
        if (mounted) {
          setError(`Failed to load object categories: ${err instanceof Error ? err.message : String(err)}`);
          setLoading(false);
        }
      }
    };

    loadCategories();

    return () => {
      mounted = false;
    };
  }, []);

  const createCategory = async (categoryData: {
    name: string;
    displayName: string;
    description?: string;
    color: string;
    parentCategoryId?: string;
  }) => {
    try {
      const newCategory: Omit<ObjectCategory, 'id'> = {
        ...categoryData,
        createdAt: serverTimestamp(),
        createdBy: 'admin', // TODO: Get from auth context
        updatedAt: serverTimestamp(),
        active: true
      };

      const docRef = await addDoc(collection(db, 'objectCategories'), newCategory);
      return docRef.id;
    } catch (err) {
      console.error('Error creating object category:', err);
      throw err;
    }
  };

  const updateCategory = async (categoryId: string, updates: Partial<ObjectCategory>) => {
    try {
      await updateDoc(doc(db, 'objectCategories', categoryId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error updating object category:', err);
      throw err;
    }
  };

  const deleteCategory = async (categoryId: string) => {
    try {
      await deleteDoc(doc(db, 'objectCategories', categoryId));
    } catch (err) {
      console.error('Error deleting object category:', err);
      throw err;
    }
  };

  return {
    categories,
    loading,
    error,
    createCategory,
    updateCategory,
    deleteCategory
  };
}

